import { useRef, useState } from "react";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Converts text between different capitalization styles — uppercase, lowercase, Title Case, Sentence case, and others — without retyping anything by hand. Useful for fixing text that was pasted in from somewhere with inconsistent capitalization, or matching a specific style requirement for a heading, title, or form field.`,
  how: `Paste your text, select the case style you want, and copy or download the converted result. Each button converts the text you typed, not the previous result, so you can try several styles in a row; "Restore original" brings your text back.`,
  tip: `Title Case tools generally lowercase small connecting words (like "a," "the," and "and") automatically — check the result against your specific style guide if you need exact control over which words get capitalized, since different publications and style guides don't always agree on which short words should stay lowercase.`,
};

// Unicode-aware word-start matcher. The original `\w\S*` and `[a-z]`
// patterns are ASCII-only -- \b and \w don't treat an accented letter as
// a word character, so a word starting with one (e.g. "éclair") gets its
// capital placed on the *second* letter instead of the first. \p{L}
// matches any Unicode letter, in any script.
// Small connecting words a Title Case keeps lowercase (except as the first or last
// word of a line) -- the page's own tip has always said it does; it capitalized
// them ("To", "In", "A") until QA's retest pointed out the mismatch.
const SMALL_WORDS = new Set(["a", "an", "the", "and", "but", "or", "for", "nor", "on", "at", "to", "by", "in", "of", "up", "as"]);
// A word, with any apostrophes inside it (o'neil, don't) kept in one piece.
const WORD = new RegExp("[\\p{L}\\p{N}]+(?:['\u2019][\\p{L}\\p{N}]+)*", "gu");
const HAS_LOWER = new RegExp("\\p{Ll}", "u");
const HAS_UPPER = new RegExp("\\p{Lu}", "u");
const INNER_CAPITAL = new RegExp("\\p{Ll}[\\p{L}'\u2019]*\\p{Lu}", "u"); // iPhone, macOS, eBay
const IS_LETTER = new RegExp("\\p{L}", "u");
const NAME_APOSTROPHE = new RegExp("^([OD])(['\u2019])(\\p{L})", "u"); // O'Neil, D'Angelo
const MC_PREFIX = new RegExp("^Mc(\\p{L})", "u"); // McDonald

function capitalizeWord(lower: string): string {
  const [first, ...rest] = Array.from(lower);
  // A ligature such as "ﬁ" upper-cases to "FI"; only its first letter should stay capital ("Fi").
  const up = first.toLocaleUpperCase();
  let w = (up.length > 1 ? up[0] + up.slice(1).toLowerCase() : up) + rest.join("");
  w = w.replace(NAME_APOSTROPHE, (_m, a: string, ap: string, c: string) => a + ap + c.toLocaleUpperCase());
  w = w.replace(MC_PREFIX, (_m, c: string) => "Mc" + c.toLocaleUpperCase());
  return w;
}

function titleCaseWord(word: string, isEdge: boolean, inputIsMixedCase: boolean): string {
  const letters = Array.from(word).filter((c) => IS_LETTER.test(c));
  // Existing casing is only trusted when the input already has both cases --
  // a line typed ALL CAPS or all lowercase still gets a normal Title Case,
  // but "visit (NYC)" or "a iPhone" keeps NYC and iPhone as written.
  if (inputIsMixedCase && letters.length >= 2) {
    const allUpper = letters.every((c) => c === c.toLocaleUpperCase() && c !== c.toLocaleLowerCase());
    if (allUpper || INNER_CAPITAL.test(word)) return word;
  }
  const lower = word.toLocaleLowerCase();
  if (!isEdge && SMALL_WORDS.has(lower)) return lower;
  return capitalizeWord(lower);
}

export function titleCase(t: string): string {
  const inputIsMixedCase = HAS_LOWER.test(t) && HAS_UPPER.test(t);
  return t
    .split("\n")
    .map((line) => {
      const words = Array.from(line.matchAll(WORD));
      if (words.length === 0) return line;
      let out = "";
      let cursor = 0;
      words.forEach((m, i) => {
        const at = m.index ?? 0;
        out += line.slice(cursor, at) + titleCaseWord(m[0], i === 0 || i === words.length - 1, inputIsMixedCase);
        cursor = at + m[0].length;
      });
      return out + line.slice(cursor);
    })
    .join("\n");
}

/** Does this word keep its own capitals (NASA, iPhone) because the surrounding text is mixed case? */
function keepsOwnCase(word: string, inputIsMixedCase: boolean): boolean {
  if (!inputIsMixedCase) return false;
  const letters = Array.from(word).filter((c) => IS_LETTER.test(c));
  if (letters.length < 2) return false;
  const allUpper = letters.every((c) => c === c.toLocaleUpperCase() && c !== c.toLocaleLowerCase());
  return allUpper || INNER_CAPITAL.test(word);
}

/** Capital at the start of the text, of each line and after . ! ? -- leading spaces are skipped, and
 *  acronyms and brand spellings are kept the same way Title Case keeps them. */
export function sentenceCase(t: string): string {
  const inputIsMixedCase = HAS_LOWER.test(t) && HAS_UPPER.test(t);
  let out = "";
  let cursor = 0;
  let atStart = true;
  for (const m of Array.from(t.matchAll(WORD))) {
    const at = m.index ?? 0;
    const gap = t.slice(cursor, at);
    out += gap;
    if (/[.!?]["'\u201d\u2019)\]]*\s|\n/.test(gap)) atStart = true;
    const word = m[0];
    const lower = word.toLocaleLowerCase();
    let next: string;
    if (lower === "i") next = "I";
    else if (/^i['\u2019](?:m|ll|ve|d)$/.test(lower)) next = "I" + lower.slice(1);
    else if (keepsOwnCase(word, inputIsMixedCase)) next = word;
    else next = atStart ? capitalizeFirst(lower) : lower;
    out += next;
    atStart = false;
    cursor = at + word.length;
  }
  return out + t.slice(cursor);
}

function capitalizeFirst(lower: string): string {
  const [first, ...rest] = Array.from(lower);
  const up = first.toLocaleUpperCase();
  return (up.length > 1 ? up[0] + up.slice(1).toLowerCase() : up) + rest.join("");
}

function alternatingCase(t: string): string {
  // Iterate graphemes, not UTF-16 code units -- split("") breaks emoji
  // and other multi-unit characters into unpaired surrogates. Also
  // skips any character whose uppercase form isn't the same length
  // (e.g. German "ß" -> "SS"), since swapping a 1-char input for a
  // 2-char output silently changes the text's length and meaning.
  const graphemes = Array.from(
    new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(t),
    (s) => s.segment,
  );
  return graphemes
    .map((c, i) => {
      const target = i % 2 === 0 ? c.toLowerCase() : c.toUpperCase();
      return target.length === c.length ? target : c;
    })
    .join("");
}

/**
 * Words for the programmer cases. Splits on anything that isn't a letter or number AND inside camelCase and
 * acronyms: XMLHttpRequest -> xml, http, request; getHTTPResponse -> get, http, response; userID -> user, id.
 * Apostrophes are dropped first ("fox's" -> "foxs") and a dotted capital I (from Turkish İ) becomes plain i.
 */
const CAMEL_BREAK = new RegExp("([\\p{Ll}\\p{N}])(\\p{Lu})", "gu");
const ACRONYM_BREAK = new RegExp("(\\p{Lu})(\\p{Lu}\\p{Ll})", "gu");
export function caseWords(t: string): string[] {
  return (
    t
      .replace(/['\u2019]/g, "")
      .replace(/\u0130/g, "I")
      .replace(/i\u0307/g, "i")
      .match(new RegExp("[\\p{L}\\p{N}\\p{M}]+", "gu"))
      ?.flatMap((chunk) =>
        chunk
          .replace(CAMEL_BREAK, "$1 $2")
          .replace(ACRONYM_BREAK, "$1 $2")
          .split(" ")
          .map((w) => w.toLocaleLowerCase()),
      ) ?? []
  );
}
const capitalize = (w: string) => w[0].toLocaleUpperCase() + w.slice(1);
export const snakeCase = (t: string) => caseWords(t).join("_");
export const kebabCase = (t: string) => caseWords(t).join("-");
export const pascalCase = (t: string) => caseWords(t).map(capitalize).join("");
export const constantCase = (t: string) => caseWords(t).join("_").toLocaleUpperCase();

export function camelCase(t: string): string {
  const words = caseWords(t);
  if (words.length === 0) return "";
  return words[0] + words.slice(1).map(capitalize).join("");
}

/**
 * What a case button converts. After a conversion `original` holds the text that was typed, and every
 * further button converts THAT -- not the previous output -- so trying several cases in a row compares them
 * (aLtErNaTiNg then snake_case is "hello_world", not "h_el_lo_w_or_ld"). Editing the box drops `original`.
 */
export function conversionSource(original: string | null, inBox: string): string {
  return original ?? inBox;
}

export default function CaseConverter() {
  const [text, setText] = useState("");
  const [original, setOriginal] = useState<string | null>(null);
  const { logUsageNow } = useLogUsageOnAction();
  // The text in the box right now, for use after an await (see convert()).
  const textRef = useRef(text);
  textRef.current = text;
  // The typed text, readable inside convert() after its await (state would be stale by then).
  const originalRef = useRef<string | null>(original);
  originalRef.current = original;

  const conversions = [
    { label: "UPPERCASE", fn: (t: string) => t.toUpperCase() },
    { label: "lowercase", fn: (t: string) => t.toLowerCase() },
    { label: "Title Case", fn: titleCase },
    { label: "Sentence case", fn: sentenceCase },
    { label: "aLtErNaTiNg", fn: alternatingCase },
    { label: "camelCase", fn: camelCase },
    { label: "PascalCase", fn: pascalCase },
    { label: "snake_case", fn: snakeCase },
    { label: "kebab-case", fn: kebabCase },
    { label: "CONSTANT_CASE", fn: constantCase },
  ];

  const copy = () => { void copyText(text, "Copied!"); };

  // Was a hardcoded `useLogToolUsage(true, 3000, { ownActions: true })` -- a discrete
  // button-click action, not a continuously-live tool, so it only ever
  // logged/nagged 3 seconds after the fact and never actually gated the
  // click. Confirmed as a real bug: clicking a conversion at the daily
  // limit produced the converted text with no paywall at all.
  // logUsageNow() is the same gating pattern Merge PDF already uses
  // correctly -- await it first, and don't convert if it returns false.
  const convert = async (fn: (t: string) => string) => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    // Preserve the original on the FIRST conversion of a given input
    // only, so chaining two conversions (e.g. UPPERCASE then Title
    // Case) still offers a way back to what was actually typed, not
    // just the previous conversion's output.
    // Convert what is in the box NOW, not what was there at click time. The gate
    // normally answers instantly, but can take a moment (first visit, confirming
    // a block) -- and QA reproduced a late result replacing text typed in the
    // meantime ("first text typed" -> click UPPERCASE -> type more -> the box
    // became "FIRST TEXT TYPED"). CC-10.
    const current = conversionSource(originalRef.current, textRef.current);
    originalRef.current = current;
    setOriginal(current);
    setText(fn(current));
  };

  // The result as shown in the box. The conversion already counted, so saving the file costs nothing more (same as Copy).
  const download = () => downloadBlob(new Blob([text], { type: "text/plain;charset=utf-8" }), "converted-text.txt", { free: true });

  const restore = () => {
    if (original === null) return;
    setText(original);
    setOriginal(null);
  };

  return (
    <ToolContainer>
      <Textarea
        placeholder="Type or paste your text here..."
        value={text}
        onChange={e => { setText(e.target.value); setOriginal(null); }}
        className="min-h-[150px] text-base"
      />
      <div className="flex flex-wrap gap-2 mt-4">
        {conversions.map(c => (
          <Button key={c.label} variant="outline" size="sm" onClick={() => convert(c.fn)}>{c.label}</Button>
        ))}
        <Button size="sm" onClick={copy} disabled={!text}>Copy</Button>
        <Button size="sm" variant="outline" onClick={download} disabled={!text}>Download .txt</Button>
        {original !== null && (
          <Button size="sm" variant="ghost" onClick={restore}>Restore original</Button>
        )}
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
